import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 10000,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Request interceptor ready for future use
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    // Reset network error count on successful response
    sessionStorage.removeItem("network-error-count");
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      const { status, data } = error.response;

      switch (status) {
        case 401:
          // Unauthorized - redirect to login only if not already on public pages
          const publicPages = ["/login", "/register"];
          if (!publicPages.includes(window.location.pathname)) {
            console.warn(
              "Unauthorized - session expired, clearing cache and redirecting to login"
            );
            // Clear all auth state
            localStorage.clear();
            sessionStorage.clear();

            // Clear all cookies
            document.cookie.split(";").forEach((c) => {
              document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
            });

            // Force reload to login page (this clears React Query cache)
            window.location.replace("/login");
          }
          break;

        case 403:
          // Forbidden
          console.error("Access forbidden:", data?.message || "Forbidden");
          break;

        case 404:
          // Not found
          console.error("Resource not found:", data?.message || "Not found");
          break;

        case 422:
          // Validation error
          console.error("Validation error:", data?.errors || data?.message);
          break;

        case 500:
          // Server error
          console.error(
            "Server error:",
            data?.message || "Internal server error"
          );
          break;

        default:
          console.error("API error:", data?.message || "Unknown error");
      }
    } else if (error.request) {
      // Request was made but no response
      console.error("Network error: No response from server");

      // If it's a network error and we're trying to access protected routes
      // it might be because the session is invalid
      const publicPages = ["/login", "/register", "/api-test", "/db-viewer"];
      if (!publicPages.includes(window.location.pathname)) {
        // Check if it's repeated network errors (possible stale session)
        const errorCount = parseInt(
          sessionStorage.getItem("network-error-count") || "0"
        );
        sessionStorage.setItem("network-error-count", String(errorCount + 1));

        // After 3 network errors, clear state and reload
        if (errorCount >= 2) {
          console.warn(
            "Multiple network errors detected - clearing cache and reloading"
          );
          sessionStorage.removeItem("network-error-count");
          localStorage.clear();
          sessionStorage.clear();
          window.location.replace("/login");
        }
      }
    } else {
      // Something else happened
      console.error("Error:", error.message);
    }

    return Promise.reject(error);
  }
);

export default api;
